import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import StatusPill from '../../components/ui/StatusPill';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import {
  getActivityFeed,
  getAttentionCounts,
  getDashboardSummary,
  getTodaysSessions,
} from '../../actions/dashboardAction';
import { statusTone } from '../../design/tokens';
import './dashboard.css';

const QUEUES = [
  { key: 'partner_approvals', label: 'Partner approvals', path: '/agent-onboarding' },
  { key: 'bank_approvals', label: 'Bank approvals', path: '/agent-bank-information' },
  { key: 'service_actions', label: 'Service actions', path: '/agent-service-actions' },
  { key: 'order_follow_ups', label: 'Order follow-ups', path: '/order-follow-up' },
  { key: 'open_tickets', label: 'Open tickets', path: '/support-ticket' },
];

function formatValue(value, format) {
  if (value == null) return '—';
  if (format === 'currency') {
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  return Number(value).toLocaleString('en-IN');
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function KpiTile({ kpi, inverted }) {
  const deltaPositive = kpi.delta != null && kpi.delta >= 0;
  return (
    <div className={`sv-kpi ${inverted ? 'is-inverted' : ''}`}>
      <div className="sv-kpi__label">{kpi.label}</div>
      <div className="sv-kpi__value-row">
        <span className="sv-kpi__value">{formatValue(kpi.value, kpi.format)}</span>
        {kpi.delta != null && (
          <span className={`sv-kpi__delta ${deltaPositive ? 'is-up' : 'is-down'}`}>
            {deltaPositive ? '+' : '−'}
            {Math.abs(kpi.delta)}%
          </span>
        )}
      </div>
      {kpi.context && <div className="sv-kpi__context">{kpi.context}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [counts, setCounts] = useState({});
  const [sessions, setSessions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, c, t, a] = await Promise.all([
        getDashboardSummary(30),
        getAttentionCounts(),
        getTodaysSessions(8),
        getActivityFeed(12),
      ]);
      if (cancelled) return;
      setSummary(s);
      setCounts(c);
      setSessions(t);
      setActivity(a);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = summary?.kpis || [];

  return (
    <>
      <PageHeader
        section="Overview"
        title="Dashboard"
        subtitle="What needs attention today, and how the business is tracking."
      />

      <div className="sv-screen-body sv-dashboard">
        <div className="sv-kpi-grid">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div className="sv-kpi" key={i}>
                  <SkeletonBlock width="55%" height={11} />
                  <div style={{ marginTop: 12 }}>
                    <SkeletonBlock width="70%" height={26} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <SkeletonBlock width="85%" height={10} />
                  </div>
                </div>
              ))
            : kpis.map((kpi, index) => (
                <KpiTile key={kpi.key} kpi={kpi} inverted={index === 0} />
              ))}
        </div>

        <div className="sv-dashboard__split">
          <section className="sv-card sv-panel">
            <header className="sv-panel__head">
              <h2 className="sv-card__title">Needs your attention</h2>
            </header>
            <div className="sv-panel__body">
              {QUEUES.map((queue) => {
                const count = counts[queue.key] || 0;
                return (
                  <button
                    type="button"
                    key={queue.key}
                    className="sv-queue-row"
                    onClick={() => navigate(queue.path)}
                  >
                    <span className={`sv-queue-row__count ${count > 0 ? 'is-attention' : ''}`}>
                      {count}
                    </span>
                    <span className="sv-queue-row__label">{queue.label}</span>
                    <span className="sv-queue-row__link">
                      Review <i className="bx bx-chevron-right" aria-hidden="true" />
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="sv-card sv-panel">
            <header className="sv-panel__head">
              <h2 className="sv-card__title">Today&rsquo;s sessions</h2>
              <button type="button" className="sv-panel__link" onClick={() => navigate('/sessions')}>
                View all
              </button>
            </header>
            <div className="sv-panel__body">
              {!loading && sessions.length === 0 && (
                <div className="sv-panel__empty">No sessions scheduled today.</div>
              )}
              {sessions.map((session) => (
                <div className="sv-session-row" key={session.id}>
                  <span className="sv-session-row__time sv-mono">
                    {formatTime(session.start_time)}
                  </span>
                  <div className="sv-session-row__text">
                    <div className="sv-session-row__service sv-truncate">
                      {session.service || 'Session'}
                    </div>
                    <div className="sv-session-row__partner sv-truncate">{session.partner}</div>
                  </div>
                  <StatusPill status={session.status} />
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="sv-card sv-panel">
          <header className="sv-panel__head">
            <h2 className="sv-card__title">Recent activity</h2>
          </header>
          <div className="sv-panel__body">
            {!loading && activity.length === 0 && (
              <div className="sv-panel__empty">Nothing has happened yet today.</div>
            )}
            {activity.map((entry) => (
              <div className="sv-activity-row" key={entry.id}>
                <span
                  className="sv-activity-row__dot"
                  data-tone={statusTone(entry.status)}
                  aria-hidden="true"
                />
                <span className="sv-activity-row__text sv-truncate">
                  {entry.actor && <strong>{entry.actor}</strong>} {entry.message || entry.sub_type}
                </span>
                <span className="sv-activity-row__time sv-mono">
                  {relativeTime(entry.created_at)}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
